import React, { useState } from "react";
import { Link } from "react-router-dom";
import PageTopSlider from "../components/PageTopSlider";
import SEO from "../components/SEO";

const SERVICES = [
  {
    id: "energimaerkning",
    title: "Energimærkning",
    desc: "Lovpligtig mærkning ved salg, udlejning eller tilskud.",
    icon: "fa-certificate",
  },
  {
    id: "trykprovning",
    title: "Trykprøvning / BlowerDoor",
    desc: "Tæthedsprøvning til nybyggeri eller tjek for utætheder.",
    icon: "fa-wind",
  },
  {
    id: "termografi",
    title: "Termografi",
    desc: "Infrarødt varmekamera afslører kuldebroer og varmetab.",
    icon: "fa-temperature-arrow-down",
  },
  {
    id: "energiramme",
    title: "Energirammeberegning",
    desc: "Teknisk beregning til byggetilladelse & bygningsreglement.",
    icon: "fa-compass-drafting",
  },
];

export default function Prisberegner() {
  const [step, setStep] = useState(1);

  // Selections state (supports multiple services selected at once)
  const [selectedServices, setSelectedServices] = useState(["energimaerkning"]);
  const [propertyType, setPropertyType] = useState("villa");
  const [propertySize, setPropertySize] = useState("medium"); // small (<100), medium (100-199), large (200-299), xlarge (300+)
  const [purpose, setPurpose] = useState("salg");

  // Contact form submission state after calculation
  const [contactInfo, setContactInfo] = useState({
    navn: "",
    telefon: "",
    email: "",
    adresse: "",
    bemaerkninger: "",
    website_url: "", // Honeypot
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Helper to get ET Danmark normal prices
  function getServiceNormalPrice(serviceId, size = propertySize) {
    if (serviceId === "energimaerkning") {
      if (propertyType === "erhverv") return null;
      if (propertyType === "flerfamilie") {
        if (size === "ff_0_499") return 6688;
        if (size === "ff_500_999") return 8088;
        if (size === "ff_1000_1999") return 9288;
        if (size === "ff_2000_2999") return 12388;
        if (size === "ff_3000_4999") return 14888;
        if (size === "ff_5000_plus") return null;
        return 6688;
      }
      if (size === "small") return 4188;
      if (size === "medium") return 4488;
      if (size === "large") return 4888;
      if (size === "xlarge") return 5500;
      return 4188;
    }
    if (serviceId === "trykprovning") {
      if (propertyType === "erhverv" || propertyType === "flerfamilie") return null;
      if (size === "small" || size === "medium" || size === "large" || size === "xlarge") return 3300;
      return null;
    }
    if (serviceId === "termografi") {
      return 2500;
    }
    return 0;
  }

  function toggleService(serviceId) {
    setSelectedServices((prev) => {
      if (prev.includes(serviceId)) {
        return prev.filter((id) => id !== serviceId);
      } else {
        return [...prev, serviceId];
      }
    });
  }

  const isKombiSelected =
    selectedServices.includes("energimaerkning") && selectedServices.includes("trykprovning");

  function toggleKombi() {
    if (isKombiSelected) {
      setSelectedServices((prev) =>
        prev.filter((id) => id !== "energimaerkning" && id !== "trykprovning")
      );
    } else {
      setSelectedServices((prev) => {
        const next = new Set(prev);
        next.add("energimaerkning");
        next.add("trykprovning");
        return Array.from(next);
      });
    }
  }

  // Pricing calculation based strictly on ET Danmark actual prices
  function calculatePrice() {
    if (selectedServices.length === 0) {
      return {
        amount: null,
        formatted: "Vælg venligst ydelse",
        note: "Vælg mindst én ydelse i Trin 1 for at beregne prisen.",
        requiresCustomQuote: false,
      };
    }

    // REGEL: Vælger man energiramme beregning så skal den ALTID sige "Gratis uforpligtende tilbud"
    if (selectedServices.includes("energiramme")) {
      return {
        amount: null,
        formatted: "Gratis uforpligtende tilbud",
        note: "Energirammeberegninger udregnes på baggrund af tegningsmateriale. Vi udarbejder et gratis og uforpligtende tilbud på opgaven.",
        requiresCustomQuote: true,
      };
    }

    // If commercial, requires custom quote
    if (propertyType === "erhverv") {
      return {
        amount: null,
        formatted: "Kontakt os for pris",
        note: "Større ejendomme og erhvervsbyggeri prissættes individuelt.",
        requiresCustomQuote: true,
      };
    }

    let originalTotal = 0;
    let customQuoteNeeded = false;

    for (const serviceId of selectedServices) {
      const p = getServiceNormalPrice(serviceId, propertySize);
      if (p === null) {
        customQuoteNeeded = true;
      } else {
        originalTotal += p;
      }
    }

    if (customQuoteNeeded) {
      return {
        amount: null,
        originalAmount: null,
        discount: 0,
        formatted: "Kontakt os for pris",
        originalFormatted: null,
        savingsFormatted: null,
        note: "Større ejendomme og erhvervsbyggeri prissættes individuelt.",
        requiresCustomQuote: true,
        isKombi: isKombiSelected,
      };
    }

    // Kombi-pakke rabat: Energimærkning + Trykprøvning spar 600 kr.
    // 0-99 m²: 7.488 kr. - 600 kr. = 6.888 kr.
    // 100-199 m²: 7.788 kr. - 600 kr. = 7.188 kr.
    // 200-299 m²: 8.588 kr. - 600 kr. = 7.988 kr.
    // 300+ m²: fra 9.188 kr. - 600 kr. = fra 8.588 kr.
    const discount = isKombiSelected ? 600 : 0;
    const finalTotal = Math.max(0, originalTotal - discount);

    return {
      amount: finalTotal,
      originalAmount: originalTotal,
      discount: discount,
      formatted: `Fra ${finalTotal.toLocaleString("da-DK")} kr.`,
      originalFormatted: `Fra ${originalTotal.toLocaleString("da-DK")} kr.`,
      savingsFormatted: `${discount.toLocaleString("da-DK")} kr.`,
      note: isKombiSelected
        ? "Kombipris inkl. moms, kørsel i Jylland & Fyn og begge rapporter. Du sparer 600 kr. ved at bestille samlet!"
        : "Vejledende fra-pris inkl. moms, kørsel i Jylland & Fyn og komplet rapport.",
      requiresCustomQuote: false,
      isKombi: isKombiSelected,
    };
  }

  const calculation = calculatePrice();

  // Summary text for the lead submission
  function getSummaryDetails() {
    const serviceNames = selectedServices.map((id) => {
      const found = SERVICES.find((s) => s.id === id);
      return found ? found.title : id;
    });

    const typeName =
      propertyType === "villa"
        ? "Enfamiliehus / Villa"
        : propertyType === "raekkehus"
        ? "Rækkehus / Kædehus"
        : propertyType === "sommerhus"
        ? "Sommerhus"
        : propertyType === "nybyggeri"
        ? "Nybyggeri"
        : propertyType === "flerfamilie"
        ? "Flerfamiliehus / Etageejendom"
        : "Erhverv / Større ejendom";

    const sizeName =
      propertySize === "small"
        ? "Under 100 m²"
        : propertySize === "medium"
        ? "100 - 199 m²"
        : propertySize === "large"
        ? "200 - 299 m²"
        : propertySize === "xlarge"
        ? "300 m² eller derover"
        : propertySize === "ff_0_499"
        ? "0–499 m²"
        : propertySize === "ff_500_999"
        ? "500–999 m²"
        : propertySize === "ff_1000_1999"
        ? "1.000–1.999 m²"
        : propertySize === "ff_2000_2999"
        ? "2.000–2.999 m²"
        : propertySize === "ff_3000_4999"
        ? "3.000–4.999 m²"
        : "5.000 m² og derover";

    const purposeName =
      purpose === "salg"
        ? "Salg / Udlejning"
        : purpose === "optimering"
        ? "Energioptimering / Spare varme"
        : "Myndighedsgodkendelse (BBR)";

    const kombiNote = isKombiSelected
      ? ` (Inkl. Kombipakke: Energimærkning & Trykprøvning – Besparelse: 600 kr. inkl. moms)`
      : "";

    const savingsNote = calculation.discount > 0
      ? ` (Normalpris: ${calculation.originalFormatted}, Du sparer: ${calculation.savingsFormatted} inkl. moms)`
      : "";

    return `Ydelser: ${serviceNames.join(", ")}${kombiNote} | Boligtype: ${typeName} | Størrelse: ${sizeName} | Formål: ${purposeName} | Estimeret fra-pris: ${calculation.formatted}${savingsNote}`;
  }

  async function handleLeadSubmit(e) {
    e.preventDefault();
    const errs = {};
    if (!contactInfo.navn.trim()) errs.navn = "Angiv venligst dit navn";
    if (!contactInfo.telefon.trim()) errs.telefon = "Angiv venligst dit telefonnummer";
    if (!contactInfo.email.trim()) errs.email = "Angiv venligst din email";

    if (Object.keys(errs).length > 0) {
      setFormErrors(errs);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    const payload = {
      navn: contactInfo.navn,
      telefon: contactInfo.telefon,
      email: contactInfo.email,
      adresse: contactInfo.adresse,
      beskrivelse: `BESTILLING / FORESPØRGSEL FRA PRISBEREGNER:\n${getSummaryDetails()}\n\nBrugers bemærkning:\n${contactInfo.bemaerkninger}`,
      website_url: contactInfo.website_url, // Honeypot
    };

    try {
      const res = await fetch("/_api/webform/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }).catch(() =>
        fetch("https://api.dev.auxo.dk/webform/submit", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        })
      );

      if (!res.ok) {
        throw new Error("Kunne ikke sende forespørgslen. Prøv venligst igen.");
      }

      // Google Tag / GTM conversion event
      if (typeof window !== "undefined" && window.dataLayer) {
        window.dataLayer.push({
          event: "generate_lead",
          form_name: "Prisberegner",
          value: calculation?.amount && calculation.amount > 0 ? calculation.amount : undefined,
          currency: "DKK",
        });
      }

      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || "Der opstod en fejl. Ring evt. direkte på 42 49 95 95.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-[#FAF9F6] min-h-screen">
      <SEO
        title="Prisberegner | Beregn pris på energimærkning & trykprøvning | ET Danmark ApS"
        description="Beregn hurtigt og uforpligtende din pris på energimærkning, trykprøvning og energiramme online hos ET Danmark ApS."
        canonical="https://www.etdanmark.com/prisberegner"
        ogImage="https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07506.jpg"
      />
      {/* 1. TOP SLIDER HERO */}
      <PageTopSlider
        title="Beregn din pris online"
        subtitle="Få et hurtigt overblik over prisen på energimærkning, trykprøvning eller termografi. Beregningen er baseret på vores gennemsigtige fra-priser inkl. moms."
        badge="Uforpligtende online prisberegner"
        breadcrumbs={[
          { label: "Forside", to: "/" },
          { label: "Prisberegner" },
        ]}
        actions={
          <a
            href="tel:+4542499595"
            className="px-6 py-3 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 text-white font-bold text-sm backdrop-blur-xs transition-all flex items-center gap-2"
          >
            <i className="fa-solid fa-phone text-emerald-300 text-xs" aria-hidden="true"></i>
            <span>Spørgsmål? Ring 42 49 95 95</span>
          </a>
        }
        image={{
          url: "https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07506.jpg",
          alt: "Beregn pris på energirådgivning",
        }}
      />

      <div className="py-10 sm:py-14">
        {/* Step Progress Bar */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 mb-8">
          <div className="grid grid-cols-4 gap-2 text-xs font-semibold">
            {[
              { num: 1, label: "1. Vælg ydelse" },
              { num: 2, label: "2. Boligtype" },
              { num: 3, label: "3. Areal (m²)" },
              { num: 4, label: "4. Pris & Bestilling" },
            ].map((s) => (
              <div
                key={s.num}
                onClick={() => step > s.num && setStep(s.num)}
                className={`pb-2 border-b-3 transition-colors ${
                  step >= s.num
                    ? "border-[#165B4C] text-[#165B4C]"
                    : "border-stone-200 text-stone-400"
                } ${step > s.num ? "cursor-pointer" : ""}`}
              >
                <span className="hidden sm:inline">{s.label}</span>
                <span className="sm:hidden">Trin {s.num}</span>
              </div>
            ))}
          </div>
        </div>

      {/* Main Calculator Card */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left Column: Form Steps (8 cols) */}
          <div className="lg:col-span-8 bg-white rounded-2xl p-6 sm:p-8 border border-stone-200/80 shadow-sm">
            {/* STEP 1: Ydelse */}
            {step === 1 && (
              <div className="space-y-6">
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h2 className="text-xl font-bold text-stone-900">
                      Trin 1: Hvilke ydelser har du brug for?
                    </h2>
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
                      Flervalg muligt
                    </span>
                  </div>
                  <p className="text-stone-500 text-sm mt-1">
                    Vælg én eller flere ydelser. Du kan frit kombinere målinger og rådgivning på samme ejendom.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {SERVICES.map((item) => {
                    const isSelected = selectedServices.includes(item.id);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => toggleService(item.id)}
                        className={`text-left p-4 rounded-xl border-2 transition-all cursor-pointer ${
                          isSelected
                            ? "border-[#165B4C] bg-emerald-50/40 shadow-xs ring-1 ring-[#165B4C]/20"
                            : "border-stone-200 hover:border-stone-300 bg-white"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                                isSelected
                                  ? "bg-[#165B4C] text-white"
                                  : "bg-stone-100 text-stone-600"
                              }`}
                            >
                              <i className={`fa-solid ${item.icon}`} aria-hidden="true"></i>
                            </div>
                            <div>
                              <p className="font-bold text-sm text-stone-900">{item.title}</p>
                              <p className="text-xs text-stone-500 mt-0.5">{item.desc}</p>
                            </div>
                          </div>
                          <div
                            className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
                              isSelected
                                ? "border-[#165B4C] bg-[#165B4C] text-white"
                                : "border-stone-300 bg-white"
                            }`}
                          >
                            {isSelected && (
                              <i className="fa-solid fa-check text-[10px]" aria-hidden="true"></i>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Hurtigvalg: Kombi-pakke */}
                <div
                  className={`p-4 sm:p-5 rounded-2xl border-2 transition-all ${
                    isKombiSelected
                      ? "bg-emerald-50/70 border-[#165B4C] shadow-xs ring-1 ring-[#165B4C]/20"
                      : "bg-stone-50/90 border-stone-200 hover:border-emerald-300"
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-start gap-3.5">
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                          isKombiSelected
                            ? "bg-[#165B4C] text-white"
                            : "bg-emerald-100 text-emerald-800"
                        }`}
                      >
                        <i className="fa-solid fa-boxes-packing text-base" aria-hidden="true"></i>
                      </div>
                      <div className="space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-bold text-sm text-stone-900">
                            Populær Kombipakke: Energimærkning & Trykprøvning
                          </p>
                          <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-emerald-700 text-white shadow-xs">
                            Spar 600 kr. inkl. moms
                          </span>
                        </div>
                        <p className="text-xs text-stone-600">
                          Få udført begge undersøgelser samlet under samme besøg og spar 600 kr.
                        </p>
                        <div className="flex flex-wrap items-center gap-3 text-xs pt-1">
                          <span className="text-stone-400 line-through">
                            Normalpris: fra 7.488 kr.
                          </span>
                          <span className="font-extrabold text-[#165B4C] text-sm">
                            Kombipris: Fra 6.888 kr. inkl. moms
                          </span>
                          <span className="text-emerald-800 font-semibold bg-emerald-100/90 px-2 py-0.5 rounded text-[11px]">
                            Besparelse: 600 kr.
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={toggleKombi}
                      className={`px-4 py-2.5 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap shrink-0 shadow-sm flex items-center justify-center gap-2 ${
                        isKombiSelected
                          ? "bg-[#165B4C] text-white hover:bg-[#11463A]"
                          : "bg-emerald-600 hover:bg-emerald-700 text-white"
                      }`}
                    >
                      {isKombiSelected ? (
                        <>
                          <i className="fa-solid fa-circle-check text-xs" aria-hidden="true"></i>
                          <span>Kombi valgt (Sparer 600 kr.)</span>
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-plus text-xs" aria-hidden="true"></i>
                          <span>Vælg kombipakke (fra 6.888 kr.)</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {selectedServices.length === 0 && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 p-2.5 rounded-lg flex items-center gap-2">
                    <i className="fa-solid fa-circle-exclamation shrink-0" aria-hidden="true"></i>
                    <span>Vælg venligst mindst én ydelse ovenfor for at fortsætte beregningen.</span>
                  </p>
                )}

                <div className="pt-4 flex justify-end">
                  <button
                    type="button"
                    disabled={selectedServices.length === 0}
                    onClick={() => setStep(2)}
                    className="px-7 py-3 rounded-full bg-[#165B4C] hover:bg-[#11463A] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm shadow transition-all flex items-center gap-2 cursor-pointer"
                  >
                    <span>Næste: Boligtype</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2: Boligtype */}
            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-stone-900">
                    Trin 2: Vælg bygningstype
                  </h2>
                  <p className="text-stone-500 text-sm mt-1">
                    Pris og tidsforbrug varierer afhængigt af boligens type og opbygning.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {[
                    { id: "villa", label: "Enfamiliehus / Villa", icon: "fa-house" },
                    { id: "raekkehus", label: "Rækkehus / Kædehus", icon: "fa-houses" },
                    { id: "sommerhus", label: "Sommerhus", icon: "fa-tree" },
                    { id: "nybyggeri", label: "Nybyggeri (under opførelse)", icon: "fa-helmet-safety" },
                    {
                      id: "flerfamilie",
                      label: "Flerfamiliehus / Etageejendom",
                      icon: "fa-city",
                    },
                    {
                      id: "erhverv",
                      label: "Erhverv / Større ejendom (kræver tilbud)",
                      icon: "fa-building",
                    },
                  ].map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        setPropertyType(item.id);
                        if (item.id === "flerfamilie" && !propertySize.startsWith("ff_")) {
                          setPropertySize("ff_0_499");
                        } else if (item.id !== "flerfamilie" && propertySize.startsWith("ff_")) {
                          setPropertySize("medium");
                        }
                      }}
                      className={`text-left p-4 rounded-xl border-2 transition-all cursor-pointer ${
                        item.colSpan ? "sm:col-span-2" : ""
                      } ${
                        propertyType === item.id
                          ? "border-[#165B4C] bg-emerald-50/40 shadow-xs"
                          : "border-stone-200 hover:border-stone-300 bg-white"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                            propertyType === item.id
                              ? "bg-[#165B4C] text-white"
                              : "bg-stone-100 text-stone-600"
                          }`}
                        >
                          <i className={`fa-solid ${item.icon}`} aria-hidden="true"></i>
                        </div>
                        <span className="font-semibold text-sm text-stone-900">{item.label}</span>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="pt-4 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="px-6 py-2.5 rounded-full border border-stone-300 hover:bg-stone-100 text-stone-700 text-sm font-medium transition-colors cursor-pointer"
                  >
                    Tilbage
                  </button>
                  <button
                    type="button"
                    onClick={() => setStep(3)}
                    className="px-7 py-3 rounded-full bg-[#165B4C] hover:bg-[#11463A] text-white font-semibold text-sm shadow transition-all flex items-center gap-2 cursor-pointer"
                  >
                    <span>Næste: Boligens areal</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            )}

            {/* STEP 3: Areal og Formål */}
            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-stone-900">
                    Trin 3: Boligens areal og formål
                  </h2>
                  <p className="text-stone-500 text-sm mt-1">
                    Angiv cirka boligareal og årsagen til opgaven.
                  </p>
                </div>

                {/* Størrelse */}
                <div>
                  <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-2">
                    Samlet opvarmet etageareal (m²)
                  </label>
                  {propertyType === "flerfamilie" ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                      {[
                        { id: "ff_0_499", label: "0–499 m²" },
                        { id: "ff_500_999", label: "500–999 m²" },
                        { id: "ff_1000_1999", label: "1.000–1.999 m²" },
                        { id: "ff_2000_2999", label: "2.000–2.999 m²" },
                        { id: "ff_3000_4999", label: "3.000–4.999 m²" },
                        { id: "ff_5000_plus", label: "5.000 m² +" },
                      ].map((sz) => (
                        <button
                          key={sz.id}
                          type="button"
                          onClick={() => setPropertySize(sz.id)}
                          className={`p-3 rounded-xl border-2 text-center text-sm font-semibold transition-all cursor-pointer ${
                            propertySize === sz.id
                              ? "border-[#165B4C] bg-emerald-50/40 text-[#165B4C]"
                              : "border-stone-200 hover:border-stone-300 text-stone-700 bg-white"
                          }`}
                        >
                          {sz.label}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                      {[
                        { id: "small", label: "Under 100 m²" },
                        { id: "medium", label: "100 - 199 m²" },
                        { id: "large", label: "200 - 299 m²" },
                        { id: "xlarge", label: "300 m² +" },
                      ].map((sz) => (
                        <button
                          key={sz.id}
                          type="button"
                          onClick={() => setPropertySize(sz.id)}
                          className={`p-3 rounded-xl border-2 text-center text-sm font-semibold transition-all cursor-pointer ${
                            propertySize === sz.id
                              ? "border-[#165B4C] bg-emerald-50/40 text-[#165B4C]"
                              : "border-stone-200 hover:border-stone-300 text-stone-700 bg-white"
                          }`}
                        >
                          {sz.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Formål */}
                <div>
                  <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-2">
                    Hvad er formålet med undersøgelsen?
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    {[
                      { id: "salg", label: "Salg / Udlejning", icon: "fa-sign-hanging" },
                      { id: "optimering", label: "Energioptimering / Spare varme", icon: "fa-leaf" },
                      { id: "godkendelse", label: "Myndighedsgodkendelse (BBR)", icon: "fa-file-shield" },
                    ].map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setPurpose(p.id)}
                        className={`p-3 rounded-xl border-2 text-left text-xs font-semibold transition-all flex items-center gap-2 cursor-pointer ${
                          purpose === p.id
                            ? "border-[#165B4C] bg-emerald-50/40 text-[#165B4C]"
                            : "border-stone-200 hover:border-stone-300 text-stone-700 bg-white"
                        }`}
                      >
                        <i className={`fa-solid ${p.icon} text-stone-500`} aria-hidden="true"></i>
                        <span>{p.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Supplerende Ydelser / Tilvalg */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider">
                      Ydelser inkluderet i beregningen
                    </label>
                    <span className="text-[11px] text-stone-500">Tilpas efter behov</span>
                  </div>
                  <div className="space-y-2">
                    {SERVICES.map((item) => {
                      const isChecked = selectedServices.includes(item.id);
                      const price = getServiceNormalPrice(item.id, propertySize);
                      return (
                        <label
                          key={item.id}
                          className={`flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer ${
                            isChecked
                              ? "border-[#165B4C] bg-emerald-50/40 shadow-xs"
                              : "border-stone-200 bg-stone-50/50 hover:bg-stone-50"
                          }`}
                        >
                          <div className="flex items-center gap-2.5">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => toggleService(item.id)}
                              className="w-4 h-4 text-[#165B4C] rounded border-stone-300 focus:ring-[#165B4C] cursor-pointer"
                            />
                            <div className="flex items-center gap-2">
                              <i className={`fa-solid ${item.icon} text-stone-500 text-xs w-4 text-center`} aria-hidden="true"></i>
                              <span className="text-sm font-medium text-stone-800">
                                {item.title}
                              </span>
                            </div>
                          </div>
                          <span
                            className={`text-xs font-semibold px-2.5 py-1 rounded shrink-0 ${
                              item.id === "energiramme"
                                ? "text-[#165B4C] bg-emerald-100/80"
                                : "text-stone-700 bg-stone-100"
                            }`}
                          >
                            {item.id === "energiramme"
                              ? "Gratis uforpligtende tilbud"
                              : price !== null
                              ? `+ fra ${price.toLocaleString("da-DK")} kr.`
                              : "Individuel pris"}
                          </span>
                        </label>
                      );
                    })}

                    {isKombiSelected && calculation.discount > 0 && (
                      <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-xs flex items-center justify-between">
                        <span className="flex items-center gap-2 text-emerald-900 font-semibold">
                          <i className="fa-solid fa-tag text-emerald-600" aria-hidden="true"></i>
                          <span>Kombirabat fratrukket (Energimærkning + Trykprøvning):</span>
                        </span>
                        <span className="font-bold text-emerald-800 bg-emerald-100 px-2.5 py-0.5 rounded-full border border-emerald-200">
                          -600 kr. inkl. moms
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="pt-4 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="px-6 py-2.5 rounded-full border border-stone-300 hover:bg-stone-100 text-stone-700 text-sm font-medium transition-colors cursor-pointer"
                  >
                    Tilbage
                  </button>
                  <button
                    type="button"
                    disabled={selectedServices.length === 0}
                    onClick={() => setStep(4)}
                    className="px-7 py-3 rounded-full bg-[#165B4C] hover:bg-[#11463A] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm shadow transition-all flex items-center gap-2 cursor-pointer"
                  >
                    <span>Se beregnet pris & bestil</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            )}

            {/* STEP 4: Resultat & Lead Formular */}
            {step === 4 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-stone-900">
                    Trin 4: Din estimerede pris & bestilling
                  </h2>
                  <p className="text-stone-500 text-sm mt-1">
                    Udfyld formularen for at reservere en tid eller få et uforpligtende tilbud tilsendt.
                  </p>
                </div>

                {/* Highlighted Price Box */}
                <div className="p-6 rounded-2xl bg-[#11463A] text-white shadow-md relative overflow-hidden">
                  <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <p className="text-xs uppercase tracking-wider text-emerald-300 font-semibold">
                          {selectedServices.includes("energiramme")
                            ? "Uforpligtende tilbud på dine valg"
                            : isKombiSelected
                            ? "Kombipris: Energimærkning & Trykprøvning"
                            : "Estimeret fra-pris for dine valg"}
                        </p>
                        {calculation.discount > 0 && (
                          <span className="px-2.5 py-0.5 rounded-full bg-emerald-400 text-emerald-950 text-[11px] font-bold">
                            Spar {calculation.savingsFormatted} inkl. moms
                          </span>
                        )}
                      </div>

                      {calculation.discount > 0 && (
                        <div className="text-xs text-stone-300 mb-0.5 flex items-center gap-2">
                          <span>Normalpris:</span>
                          <span className="line-through font-semibold text-emerald-200/80">
                            {calculation.originalFormatted}
                          </span>
                        </div>
                      )}

                      <p className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                        {calculation.formatted}
                      </p>
                      <p className="text-xs text-stone-300 mt-1">{calculation.note}</p>
                    </div>

                    <div className="text-right sm:text-right">
                      <button
                        onClick={() => setStep(1)}
                        className="text-xs text-emerald-300 hover:text-white underline cursor-pointer"
                      >
                        Ændr parametre
                      </button>
                    </div>
                  </div>

                  {/* Highlight savings banner if kombi */}
                  {calculation.discount > 0 && (
                    <div className="mt-4 pt-3 border-t border-emerald-800/80 flex flex-wrap items-center justify-between gap-2 text-xs text-emerald-200">
                      <span className="flex items-center gap-1.5">
                        <i className="fa-solid fa-check-double text-emerald-300" aria-hidden="true"></i>
                        Kombirabat automatisk fratrukket (Mærkning + Trykprøvning)
                      </span>
                      <span className="font-bold text-white bg-emerald-800/90 px-3 py-1 rounded-lg border border-emerald-600/40">
                        Besparelse: {calculation.savingsFormatted} inkl. moms
                      </span>
                    </div>
                  )}
                </div>

                {/* Confirmation or Lead form */}
                {submitted ? (
                  <div className="p-8 rounded-2xl bg-emerald-50 border border-emerald-200 text-center space-y-4">
                    <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto text-2xl">
                      <i className="fa-solid fa-circle-check" aria-hidden="true"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-emerald-950">
                      Mange tak for din henvendelse!
                    </h3>
                    <p className="text-sm text-emerald-800 max-w-md mx-auto">
                      Vi har modtaget dine oplysninger og din beregning. Vi kontakter dig snarest – normalt inden for få timer – for at aftale nærmere.
                    </p>
                    <p className="text-xs text-stone-500 pt-2">
                      Har du brug for svar med det samme? Ring til os direkte på{" "}
                      <a href="tel:+4542499595" className="font-bold text-[#165B4C] whitespace-nowrap">
                        42 49 95 95
                      </a>
                      .
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleLeadSubmit} noValidate className="space-y-4 pt-2">
                    {/* Honeypot */}
                    <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
                      <label htmlFor="website_url_calc">Venligst lad dette felt være tomt</label>
                      <input
                        type="text"
                        id="website_url_calc"
                        name="website_url"
                        value={contactInfo.website_url}
                        onChange={(e) =>
                          setContactInfo({ ...contactInfo, website_url: e.target.value })
                        }
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-stone-700 mb-1">
                          Dit fulde navn <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={contactInfo.navn}
                          onChange={(e) => {
                            setContactInfo({ ...contactInfo, navn: e.target.value });
                            if (formErrors.navn) setFormErrors({ ...formErrors, navn: null });
                          }}
                          placeholder="F.eks. Peter Mikkelsen"
                          className={`w-full px-3.5 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 ${
                            formErrors.navn
                              ? "border-red-400 bg-red-50/40"
                              : "border-stone-300 focus:border-[#165B4C]"
                          }`}
                        />
                        {formErrors.navn && (
                          <p className="text-xs text-red-600 mt-1">{formErrors.navn}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-stone-700 mb-1">
                          Telefonnummer <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          value={contactInfo.telefon}
                          onChange={(e) => {
                            setContactInfo({ ...contactInfo, telefon: e.target.value });
                            if (formErrors.telefon) setFormErrors({ ...formErrors, telefon: null });
                          }}
                          placeholder="F.eks. 42 49 95 95"
                          className={`w-full px-3.5 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 ${
                            formErrors.telefon
                              ? "border-red-400 bg-red-50/40"
                              : "border-stone-300 focus:border-[#165B4C]"
                          }`}
                        />
                        {formErrors.telefon && (
                          <p className="text-xs text-red-600 mt-1">{formErrors.telefon}</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-stone-700 mb-1">
                          Emailadresse <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          value={contactInfo.email}
                          onChange={(e) => {
                            setContactInfo({ ...contactInfo, email: e.target.value });
                            if (formErrors.email) setFormErrors({ ...formErrors, email: null });
                          }}
                          placeholder="din@email.dk"
                          className={`w-full px-3.5 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 ${
                            formErrors.email
                              ? "border-red-400 bg-red-50/40"
                              : "border-stone-300 focus:border-[#165B4C]"
                          }`}
                        />
                        {formErrors.email && (
                          <p className="text-xs text-red-600 mt-1">{formErrors.email}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-stone-700 mb-1">
                          Ejendommens adresse / postnr.
                        </label>
                        <input
                          type="text"
                          value={contactInfo.adresse}
                          onChange={(e) =>
                            setContactInfo({ ...contactInfo, adresse: e.target.value })
                          }
                          placeholder="F.eks. Bjerrevej 62, 8700 Horsens"
                          className="w-full px-3.5 py-2.5 rounded-xl border border-stone-300 text-sm focus:border-[#165B4C] focus:outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-stone-700 mb-1">
                        Evt. bemærkninger eller ønsket dato
                      </label>
                      <textarea
                        rows={2}
                        value={contactInfo.bemaerkninger}
                        onChange={(e) =>
                          setContactInfo({ ...contactInfo, bemaerkninger: e.target.value })
                        }
                        placeholder="Skriv hvis du har specielle ønsker eller en bestemt uge..."
                        className="w-full px-3.5 py-2 rounded-xl border border-stone-300 text-sm focus:border-[#165B4C] focus:outline-none"
                      />
                    </div>

                    {submitError && (
                      <div className="p-3 rounded-xl bg-red-50 text-red-700 text-xs flex items-center gap-2">
                        <i className="fa-solid fa-triangle-exclamation shrink-0" aria-hidden="true"></i>
                        <span>{submitError}</span>
                      </div>
                    )}

                    <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-60 cursor-pointer"
                      >
                        {submitting ? (
                          <>
                            <i className="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i>
                            <span>Sender forespørgsel...</span>
                          </>
                        ) : (
                          <>
                            <span>
                              {selectedServices.includes("energiramme")
                                ? "Indhent gratis uforpligtende tilbud"
                                : "Send forespørgsel nu"}
                            </span>
                            <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                          </>
                        )}
                      </button>

                      <p className="text-xs text-stone-500 text-center sm:text-right">
                        Helt uforpligtende • Svar inden for 24 timer
                      </p>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>

          {/* Right Column: Live Summary & Trust elements (4 cols) */}
          <div className="lg:col-span-4 space-y-6">
            {/* Live Selection Summary */}
            <div className="bg-white rounded-2xl p-6 border border-stone-200/80 shadow-sm">
              <h3 className="font-bold text-stone-900 text-sm uppercase tracking-wider mb-4 pb-2 border-b border-stone-100 flex items-center justify-between">
                <span>Dine valg</span>
                <span className="text-emerald-700 font-semibold text-xs">Trin {step}/4</span>
              </h3>

              <div className="space-y-3 text-xs">
                <div className="py-1 border-b border-stone-50">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-stone-500">Valgte ydelser:</span>
                    {isKombiSelected && (
                      <span className="text-[10px] font-bold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-full">
                        Kombi
                      </span>
                    )}
                  </div>
                  {selectedServices.length === 0 ? (
                    <span className="text-amber-700 italic">Ingen ydelse valgt endnu</span>
                  ) : (
                    <ul className="space-y-1.5 pl-1">
                      {selectedServices.map((id) => {
                        const s = SERVICES.find((item) => item.id === id);
                        const price = getServiceNormalPrice(id, propertySize);
                        return (
                          <li key={id} className="flex justify-between items-center text-stone-800">
                            <span className="flex items-center gap-1.5 font-medium">
                              <i className="fa-solid fa-check text-[#165B4C] text-[10px]" aria-hidden="true"></i>
                              {s?.title || id}
                            </span>
                            <span className="text-stone-500 font-normal">
                              {id === "energiramme"
                                ? "Gratis tilbud"
                                : price !== null
                                ? `Fra ${price.toLocaleString("da-DK")} kr.`
                                : "Individuel"}
                            </span>
                          </li>
                        );
                      })}

                      {calculation.discount > 0 && (
                        <li className="flex justify-between items-center text-emerald-800 font-semibold pt-1 border-t border-dashed border-stone-200">
                          <span className="flex items-center gap-1.5">
                            <i className="fa-solid fa-tag text-emerald-600 text-[10px]" aria-hidden="true"></i>
                            Kombirabat
                          </span>
                          <span className="text-emerald-700 font-bold">
                            -{calculation.savingsFormatted}
                          </span>
                        </li>
                      )}
                    </ul>
                  )}
                </div>

                <div className="flex justify-between items-center py-1 border-b border-stone-50">
                  <span className="text-stone-500">Boligtype:</span>
                  <span className="font-semibold text-stone-800 text-right">
                    {propertyType === "villa"
                      ? "Enfamiliehus / Villa"
                      : propertyType === "raekkehus"
                      ? "Rækkehus"
                      : propertyType === "sommerhus"
                      ? "Sommerhus"
                      : propertyType === "nybyggeri"
                      ? "Nybyggeri"
                      : propertyType === "flerfamilie"
                      ? "Flerfamilie"
                      : "Erhverv"}
                  </span>
                </div>

                <div className="flex justify-between items-center py-1 border-b border-stone-50">
                  <span className="text-stone-500">Areal:</span>
                  <span className="font-semibold text-stone-800 text-right">
                    {propertySize === "small"
                      ? "< 100 m²"
                      : propertySize === "medium"
                      ? "100 - 199 m²"
                      : propertySize === "large"
                      ? "200 - 299 m²"
                      : propertySize === "xlarge"
                      ? "300 m² +"
                      : propertySize === "ff_0_499"
                      ? "0–499 m²"
                      : propertySize === "ff_500_999"
                      ? "500–999 m²"
                      : propertySize === "ff_1000_1999"
                      ? "1.000–1.999 m²"
                      : propertySize === "ff_2000_2999"
                      ? "2.000–2.999 m²"
                      : propertySize === "ff_3000_4999"
                      ? "3.000–4.999 m²"
                      : "5.000 m² +"}
                  </span>
                </div>
              </div>

              {/* Estimated total in summary */}
              <div className="mt-5 pt-4 border-t border-stone-200">
                <span className="text-xs text-stone-500 block">Estimeret fra-pris:</span>

                {calculation.discount > 0 && (
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-stone-400 line-through font-medium">
                      {calculation.originalFormatted}
                    </span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
                      Spar {calculation.savingsFormatted}
                    </span>
                  </div>
                )}

                <span className="text-2xl font-bold text-[#165B4C] block mt-0.5">
                  {calculation.formatted}
                </span>

                {calculation.discount > 0 ? (
                  <div className="mt-2 p-2.5 rounded-lg bg-emerald-50 border border-emerald-200 text-[11px] text-emerald-900 flex items-center gap-1.5">
                    <i className="fa-solid fa-circle-check text-emerald-600 shrink-0" aria-hidden="true"></i>
                    <span>
                      <strong>Kombipakke aktiv:</strong> {calculation.formatted} inkl. moms (du sparer {calculation.savingsFormatted})
                    </span>
                  </div>
                ) : (
                  <p className="text-[11px] text-stone-400 mt-1">Inkl. moms & kørsel i Jylland & Fyn</p>
                )}
              </div>
            </div>

            {/* Direct Contact Callout */}
            <div className="bg-stone-100/80 rounded-2xl p-6 border border-stone-200 text-stone-800">
              <h4 className="font-bold text-sm text-stone-900 mb-2 flex items-center gap-2">
                <i className="fa-solid fa-phone-volume text-[#165B4C]" aria-hidden="true"></i>
                Vil du hellere ringe?
              </h4>
              <p className="text-xs text-stone-600 leading-relaxed mb-4">
                Få personlig rådgivning og en direkte aftale med Lars på telefonen.
              </p>
              <a
                href="tel:+4542499595"
                className="flex items-center justify-center gap-2 w-full py-2.5 bg-white hover:bg-stone-50 border border-stone-300 rounded-full text-stone-900 font-bold text-sm shadow-2xs transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-[#165B4C]" aria-hidden="true"></i>
                <span>Ring 42 49 95 95</span>
              </a>
            </div>

            {/* Trust points */}
            <div className="bg-white rounded-2xl p-5 border border-stone-200/80 space-y-3 text-xs text-stone-600">
              <div className="flex items-center gap-2.5 text-stone-800 font-semibold">
                <i className="fa-solid fa-shield-check text-emerald-600 text-sm" aria-hidden="true"></i>
                <span>20 års erfaring i byggebranchen</span>
              </div>
              <div className="flex items-center gap-2.5 text-stone-800 font-semibold">
                <i className="fa-solid fa-certificate text-emerald-600 text-sm" aria-hidden="true"></i>
                <span>Certificeret energikonsulent</span>
              </div>
              <div className="flex items-center gap-2.5 text-stone-800 font-semibold">
                <i className="fa-solid fa-truck text-emerald-600 text-sm" aria-hidden="true"></i>
                <span>Kører i hele Jylland og Fyn</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
